import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {theme} from '../../../themes/default-theme';
import ReactHtmlParser from 'react-html-parser';
import {get} from 'lodash';

import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import cx from 'clsx';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '../Table';
import CompareTable from '../../../containers/Compare/Table';

const ExpansionPanelStyled = styled(props => {
  const classes = cx(`active-bg-${props.activeBg}`, props.className);
  return <Accordion {...props} className={classes} activeBg={undefined} border={undefined}/>
})`
  
  & {
    margin: 0px !important;
    ${props => props.border === 1 && `
      border: 1px solid ${theme.palette.middleGrey};
    `}
    border-bottom: none;
    box-shadow: none;
  }
  .MuiAccordionSummary {
    &-root {
      ${props => props.size === 'small' ? `
        height: 50px;
        &.Mui-expanded {
          min-height: 50px;
        }
      ` : `
        min-height: 60px!important;
      `}
      background: ${theme.palette.lightGrey}!important;
    }
    &-expandIconWrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      ${props => props.size === 'small' ? `
        width: 24px;
        height: 24px;
      `: `
        width: 30px;
        height: 30px;
      `}
      margin-right: 0px;
      color: ${theme.palette.white};
      border: 1px solid ${theme.palette.white};
      background: ${theme.palette.darkGrey};
      border-radius: 50%;

      ${props => props.variant === 'secondary' && `
        background: #F7F8FA;
        color: ${theme.palette.skyBlue};
      `}

      ${theme.breakpoints.down('sm')} {
        margin-right: 0px;
      }
    }
  }
  &.MuiAccordion-root:last-child {
    ${props => props.border === 1 && `
      border-bottom: 1px solid ${theme.palette.middleGrey};
    `}
    border-radius: 0;
  }
  .Mui-expanded {
    &.MuiAccordionSummary-content {
      ${props => props.size === 'small' ? `
        height: 50px;
        min-height: 50px;
        padding: 15px 0;
      ` : `
        height: 60px;
      `}
    }
    background: ${theme.palette.vividBlue}!important;
    transition-duration: 0s;
    &.MuiAccordionSummary-expandIconWrapper {
      color: ${theme.palette.white};
      background: ${theme.palette.skyBlue} !important!mportant;
      transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }
    .MuiTypography-root {
      color: ${theme.palette.white};
    }
  }

  &.active-bg-middleGrey {
    // .MuiAccordionSummary-expandIconWrapper {
    //   background: ${theme.palette.lightGrey}!important;
    //   color: ${theme.palette.skyBlue};
    //   .MuiTypography-root {
    //     color: ${theme.palette.skyBlue};
    //   }
    // }
    .Mui-expanded {
      background: ${theme.palette.middleGrey}!important;
      color: ${theme.palette.charcoal};
      .MuiTypography-root {
        color: ${theme.palette.charcoal};
      }
        &.MuiAccordionSummary-expandIconWrapper,
      &.MuiAccordionSummary-expandIcon {
        color: ${theme.palette.skyBlue};
        background: ${theme.palette.lightGrey}!important;
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      }
    }
  }
`;

const TypographyHeading =  styled(props => {
  return <Typography {...props} />
})`
  & {
    ${props => props.size === 'small' ? `
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 2px;
    `: `font-size: 20px;`}
    font-weight: 800;
    display: flex;
    align-items: center;
    text-align: left;
    color: ${theme.palette.charcoal};
    ${theme.breakpoints.down('sm')} {
    ${props => props.size === 'small' ? `
      font-size: 14px;
    `: `font-size: 14px;`
    }
      font-weight: 600;
    }
  }
`;

const ExpansionPanelDetailsStyled =  styled(props => {
  return <AccordionDetails {...props} />
})`
  & {
    font-size: 16px;
    color: ${theme.palette.gunmentalGrey};
    text-align: left;
    ${theme.breakpoints.down('sm')} {
      font-size: 14px;
    }
    ${props => props.padding === 0 && `
      padding: 0;
    `}
  }
`;

export const AccordionOutput = ({
  data = [],
  onChange = () => {},
  onExpand = () => {},
  id = 'panel',
  compare = false,
  highlight = false,
  compareItems = [],
  activeBg = 'vividBlue',
  children = null,
  size = 'regular',
  defaultExpanded = false,
  border = true,
  cellWidth = null,
  showBlankCell = false,
  firstActive = false,
  variant = 'primary',
  title,
  ...itemProps
}) => {

  const [expanded, setExpanded] = React.useState(
    firstActive ? get(data, '[0].title', false) : false
  );

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    onChange(panel);
  };

  useEffect(() => {
    onExpand(expanded);
  }, [expanded, onExpand]);

  return (
    <div>
      {data && data.length > 0 ? (
        data.map((accordion, index) => (
          <AccordionItem
            key={`${id}_${index}`}
            index={index}
            id={id}
            accordion={accordion}
            expanded={expanded}
            handleChange={handleChange}
            size={size}
            variant={variant}
            border={border}
            activeBg={activeBg}
            compare={compare}
            highlight={highlight}
            compareItems={compareItems}
            cellWidth={cellWidth}
            showBlankCell={showBlankCell}
            {...itemProps}
          />
        ))
      ) : (
        <AccordionItem
          index={0}
          id={id}
          accordion={{ title }}
          expanded={expanded}
          handleChange={handleChange}
          size={size}
          variant={variant}
          border={border}
          activeBg={activeBg}
          compare={compare}
          highlight={highlight}
          compareItems={compareItems}
          cellWidth={cellWidth}
          showBlankCell={showBlankCell}
          {...itemProps}
        />
      )}
    </div>
  );
};


const AccordionItem = ({
  accordion,
  handleChange,
  id,
  index,
  expanded,
  border,
  variant,
  activeBg,
  size,
  compare,
  highlight,
  compareItems,
  cellWidth,
  showBlankCell,
  children
}) => {

  return (
    <ExpansionPanelStyled
      id={`${id}_${index}`}
      border={border ? 1 : 0}
      size={size}
      variant={variant}
      expanded={expanded === accordion.title}
      onChange={handleChange(accordion.title)}
      activeBg={activeBg}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${accordion.title}-content`}
        id={`${id}-${accordion.title}-header`}
      >
        <TypographyHeading size={size}>
          {accordion.title}
        </TypographyHeading>
      </AccordionSummary>

      <ExpansionPanelDetailsStyled padding={(accordion.table || compare) ? 0 : 1}>
        {accordion.caption && (
          <Typography>
            {ReactHtmlParser(accordion.caption)}
          </Typography>
        )}

        {accordion.table && (
          <Table
            key={`table_${id}`}
            sideBorder={false}
            {...accordion.table}
          />
        )}

        {compare && (
          <CompareTable
            rows={accordion.rows}
            highlight={highlight}
            compareItems={compareItems}
            cellWidth={cellWidth}
            showBlankCell={showBlankCell}
          />
        )}

        {children}
      </ExpansionPanelDetailsStyled>
    </ExpansionPanelStyled>
  );
};


AccordionOutput.propTypes = {
  data: PropTypes.array,
  onChange: PropTypes.func,
  onExpand: PropTypes.func,
  id: PropTypes.string,
  compare: PropTypes.bool,
  highlight: PropTypes.bool, // for compare
  compareItems: PropTypes.array,
  activeBg: PropTypes.string,
  size: PropTypes.oneOf(['small', 'regular']),
  defaultExpanded: PropTypes.bool,
  border: PropTypes.bool,
  cellWidth: PropTypes.string,
  showBlankCell: PropTypes.bool,
  firstActive: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'secondary']),
  title: PropTypes.string,
  children: PropTypes.node,
};

export default AccordionOutput;
